import React, { useState, useEffect } from "react";
import "../../styles/cards/card.css";
import PrimaryButton from "../buttons/PrimaryButton";
import Modal from "../modal/Modal";
import confirmIcon from "../../assets/img/iconamoon--shield-yes-fill.svg";
import Cookies from "js-cookie";
import axios from "axios";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Icon } from "@iconify/react/dist/iconify.js";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const AddIndexedRecords = ({ province, district, sector, siteId }) => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [showModal, setShowModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [doneAt, setDoneAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [availableBooks, setAvailableBooks] = useState([]);
  const [indexedRecords, setIndexedRecords] = useState([]);
  const [uniqueBookCategories, setUniqueBookCategories] = useState([]);
  const [activeTab, setActiveTab] = useState("approved");
  const [availableTypes, setAvailableTypes] = useState([]);
  const [editingRecordId, setEditingRecordId] = useState(null);
  const [editingYellowFormId, setEditingYellowFormId] = useState(null);
  const [yellowForms, setYellowForms] = useState([]);
  const [dataEntryClerks, setDataEntryClerks] = useState([]);
  const [formData, setFormData] = useState({
    category: "",
    count: "",
    type: "",
    assignedTo: "",
    yellowFormCount: "",
  });

  // Fetch existing indexed records
  const fetchIndexedRecords = async () => {
    try {
      setLoading(true);
      const token = Cookies.get("token");
      const response = await fetch(
        `${baseUrl}/api/index/${siteId}/indexed-records/daily`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await response.json();
      setIndexedRecords(data);
    } catch (error) {
      setError("Error fetching indexed records");
    } finally {
      setLoading(false);
    }
  };

  // Fetch daily yellow forms
  const fetchYellowForms = async () => {
    try {
      setLoading(true);
      const token = Cookies.get("token");
      const response = await fetch(
        `${baseUrl}/api/yellow-forms/${siteId}/daily`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await response.json();
      setYellowForms(data);
    } catch (error) {
      setError("Error fetching yellow forms");
    } finally {
      setLoading(false);
    }
  };

  const fetchDataEntryClerks = async () => {
    try {
      const token = Cookies.get("token");
      const response = await axios.get(
        `${baseUrl}/api/users/data-entry-clerks`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const clerks = response.data;
      setDataEntryClerks(clerks);
    } catch (error) {
      setError("Error fetching data entry clerks");
    }
  };

  useEffect(() => {
    if (showModal) {
      fetchDataEntryClerks();
    }
  }, [showModal]);

  // submit new record
  const handleAddNewRecord = async () => {
    try {
      setSubmitting(true);
      const token = Cookies.get("token");
      if (!formData.category || !formData.type || !formData.count) {
        setError("Please fill in all fields");
        return;
      }

      const requestData = {
        category: formData.category,
        count: parseInt(formData.count),
        type: formData.type,
        assignedTo: formData.assignedTo || "",
      };
      const response = await axios.post(
        `${baseUrl}/api/index/${siteId}/indexed-records`,
        requestData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.status === 201 || response.status === 200) {
        // setShowModal(false);
        // setShowConfirmationModal(true);
        await fetchIndexedRecords();
        setFormData({
          category: "",
          count: "",
          type: "",
          yellowFormCount: "",
          assignedTo: "",
        });
        setError("");
      }
    } catch (error) {
      console.error("Error creating indexed record:", error);
      setError("Error creating indexed record");
    } finally {
      setSubmitting(false);
    }
  };

  // submit new yellow form
  const handleAddNewYellowForm = async () => {
    try {
      setSubmitting(true);
      const token = Cookies.get("token");
      if (!formData.yellowFormCount) {
        setError("Please fill in all fields");
        return;
      }
      const requestData = {
        yellowFormCount: parseInt(formData.yellowFormCount),
        assignedTo: formData.assignedTo || "",
      };
      const response = await axios.post(
        `${baseUrl}/api/yellow-forms/${siteId}/daily`,
        requestData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.status === 201 || response.status === 200) {
        // setShowModal(false);
        // setShowConfirmationModal(true);
        await fetchYellowForms();
        setFormData({
          category: "",
          count: "",
          type: "",
          yellowFormCount: "",
          assignedTo: "",
        });
        setError("");
      }
    } catch (error) {
      setError("Error creating yellow form");
    } finally {
      setSubmitting(false);
    }
  };

  //update existing record
  const handleUpdateIndexedRecord = async (recordId) => {
    try {
      setSubmitting(true);
      const token = Cookies.get("token");
      const updateData = {};

      if (formData.count !== "") {
        updateData.count = parseInt(formData.count);
      } else {
        setError("Please provide a count value to update");
        return;
      }
      const response = await axios.put(
        `${baseUrl}/api/index/${siteId}/indexed-records/${recordId}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.status === 200) {
        // setShowModal(false);
        // setShowConfirmationModal(true);
        fetchIndexedRecords();
        setFormData({
          category: "",
          count: "",
          type: "",
          yellowFormCount: "",
          assignedTo: "",
        });
        setError("");
        setEditingRecordId(null);
      }
    } catch (error) {
      if (error.response) {
        switch (error.response.status) {
          case 404:
            setError("Record not found");
            break;
          case 400:
            setError(error.response.data.error || "Invalid update data");
            break;
          default:
            setError("Error updating indexed record");
        }
      } else {
        setError("Network error while updating record");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // update yellow form
  const handleUpdateYellowForm = async (recordId) => {
    try {
      setSubmitting(true);
      const token = Cookies.get("token");
      const updateData = {};

      if (formData.yellowFormCount !== "")
        updateData.yellowFormCount = parseInt(formData.yellowFormCount);

      if (Object.keys(updateData).length === 0) {
        setError("Please provide at least one value to update");
        return;
      }

      const response = await axios.put(
        `${baseUrl}/api/yellow-forms/${siteId}/daily/${recordId}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.status === 200) {
        // setShowModal(false);
        // setShowConfirmationModal(true);
        fetchYellowForms();
        setFormData({
          category: "",
          count: "",
          type: "",
          yellowFormCount: "",
          assignedTo: "",
        });
        setError("");
        setEditingRecordId(null);
      }
    } catch (error) {
      if (error.response) {
        switch (error.response.status) {
          case 404:
            setError("Record not found");
            break;
          case 400:
            setError(error.response.data.error || "Invalid update data");
            break;
          default:
            setError("Error updating yellow form");
        }
      } else {
        setError("Network error while updating record");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // delete indexed record or yellow form
  const handleDeleteIndexedRecord = async (recordId) => {
    try {
      setSubmitting(true);
      const token = Cookies.get("token");
      const endpoint =
        activeTab === "approved"
          ? `${baseUrl}/api/index/${siteId}/indexed-records/${recordId}`
          : `${baseUrl}/api/yellow-forms/${siteId}/daily/${recordId}`;
      const response = await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 200) {
        setShowDeleteConfirm(false);
        setShowDeleteSuccess(true);
        activeTab === "approved" ? fetchIndexedRecords() : fetchYellowForms();
        setError("");
        setRecordToDelete(null);
        setFormData({ category: "", count: "", type: "", yellowFormCount: "" });
      }
    } catch (error) {
      setError("Error deleting indexed record");
    } finally {
      setSubmitting(false);
    }
  };
  const handleCloseModal = (e) => {
    if (e) e.stopPropagation();
    setShowModal(false);
    setFormData({ category: "", count: "", type: "", yellowFormCount: "" });
    setError("");
    setEditingRecordId(null);
  };

  const handleButtonClick = () => {
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (activeTab === "approved" && editingRecordId) {
      await handleUpdateIndexedRecord(editingRecordId);
      setEditingRecordId(null);
    } else if (activeTab === "all" && editingYellowFormId) {
      await handleUpdateYellowForm(editingYellowFormId);
      setEditingYellowFormId(null);
    } else {
      activeTab === "approved"
        ? await handleAddNewRecord()
        : await handleAddNewYellowForm();
    }
  };

  const handleSubmitClick = () => {
    setShowModal(false);
    setShowConfirmationModal(true);
  };

  const handleCloseConfirmationModal = () => {
    setShowConfirmationModal(false);
  };

  const fetchBookData = async () => {
    try {
      setLoading(true);
      const token = Cookies.get("token");
      const response = await fetch(`${baseUrl}/api/books`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      const filteredData = data.filter((book) => book.name !== "Yellow Forms");
      setAvailableBooks(filteredData);
      const categories = [
        ...new Set(filteredData.map((book) => book.name)),
      ].map((category) =>
        category === "Special_category" ? "Special Category" : category
      );
      setUniqueBookCategories(categories);
    } catch (error) {
      setError("Error fetching book data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "approved") {
      fetchIndexedRecords();
    } else {
      fetchYellowForms();
    }
  }, [siteId, activeTab]);

  useEffect(() => {
    if (showModal && activeTab === "approved") {
      fetchBookData();
    }
  }, [showModal, activeTab]);

  // Update available types when category changes
  const handleCategoryChange = (e) => {
    const selectedCategory = e.target.value;
    setFormData((prev) => ({
      ...prev,
      category: selectedCategory,
      type: "",
    }));

    const types = availableBooks
      .filter((book) => book.name === selectedCategory)
      .map((book) => book.bookType);
    setAvailableTypes(types);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditClick = (record) => {
    if (activeTab === "approved") {
      setFormData({
        category: record.book.name,
        count: record.count || "",
        type: record.book.bookType || "",
        assignedTo: record.assignedToClerk.name || "",
        yellowFormCount: "",
      });
      setEditingRecordId(record.id);
      setEditingYellowFormId(null);
    } else {
      setFormData({
        category: "",
        count: "",
        type: "",
        yellowFormCount: record.yellowFormCount || "",
      });
      setEditingYellowFormId(record.id);
      setEditingRecordId(null);
    }
    setShowModal(true);
  };

  const handleDeleteClick = (recordId) => {
    setRecordToDelete(recordId);
    setShowDeleteConfirm(true);
  };

  const capitalize = (string) => {
    if (!string) return "";
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  return (
    <>
      <div className="new-site-container scanned-books-card indexed">
        <div className="sector-info">
          <div className="sector-name">{sector || district}</div>
          <div className="sector-label">Sector</div>
        </div>

        <div className="add-button">
          <PrimaryButton
            text="Add Indexed Records"
            onClick={handleButtonClick}
          />
        </div>

        <Modal show={showModal} onClose={handleCloseModal} confirm={true}>
          <div className="title">ADD INDEXED BOOK</div>
          <div className="content">
            <div className="form-container new-records">
              <form action="">
                <div className="scanned-books initialized">
                  <div className="address">
                    <div className="address-container">
                      <div className="address-text">Province: {province}</div>
                    </div>
                    <div className="address-container">
                      <div className="address-text">District: {district}</div>
                    </div>
                    {sector && (
                      <div className="address-container">
                        <div className="address-text">Sector: {sector}</div>
                      </div>
                    )}
                  </div>

                  <div className="tabs">
                    <div
                      className={`tab ${
                        activeTab === "approved" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("approved")}
                    >
                      CR Books
                    </div>
                    <div
                      className={`tab ${activeTab === "all" ? "active" : ""}`}
                      onClick={() => setActiveTab("all")}
                    >
                      Yellow Forms
                    </div>
                  </div>

                  <div className="data indexed">
                    {activeTab === "approved" ? (
                      <>
                        <div className="indexed-fields">
                          <div className="label">
                            <select
                              name="category"
                              value={formData.category}
                              onChange={handleCategoryChange}
                              className="form-select"
                              disabled={editingRecordId || editingYellowFormId}
                            >
                              <option value="" disabled>
                                Book category
                              </option>
                              {uniqueBookCategories.map((category) => (
                                <option
                                  key={category}
                                  value={
                                    category === "Special Category"
                                      ? "Special_category"
                                      : category
                                  }
                                >
                                  {category}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="label">
                            <select
                              name="type"
                              value={formData.type}
                              onChange={handleInputChange}
                              className="form-select"
                              disabled={
                                !formData.category ||
                                editingRecordId ||
                                editingYellowFormId
                              }
                            >
                              <option value="" disabled>
                                Book Type
                              </option>
                              {availableTypes.map((type) => (
                                <option key={type} value={type}>
                                  {capitalize(type)}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="label">
                            <input
                              placeholder="Count"
                              type="number"
                              name="count"
                              value={formData.count}
                              onChange={handleInputChange}
                              min="1"
                            />
                          </div>
                          <div className="assigned-section">
                            <span>Assigned To</span>
                            <div className="label">
                              <select
                                name="assignedTo"
                                value={formData.assignedTo}
                                onChange={handleInputChange}
                                className="form-select"
                                disabled={
                                  editingRecordId || editingYellowFormId
                                }
                              >
                                <option value="" disabled>
                                  Select Data Entry Clerk
                                </option>
                                {dataEntryClerks.map((clerk) => (
                                  <option key={clerk.id} value={clerk.name}>
                                    {clerk.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                        {error && <div className="error-message">{error}</div>}
                      </>
                    ) : (
                      <>
                        <div className="label">
                          <input
                            placeholder="Yellow Form Count"
                            type="number"
                            name="yellowFormCount"
                            value={formData.yellowFormCount}
                            onChange={handleInputChange}
                            min="1"
                          />
                        </div>
                        <div className="assigned-section">
                          <span>Assigned To</span>
                          <div className="label">
                            <select
                              name="assignedTo"
                              value={formData.assignedTo}
                              onChange={handleInputChange}
                              className="form-select"
                              disabled={editingRecordId || editingYellowFormId}
                            >
                              <option value="" disabled>
                                Select Data Entry Clerk
                              </option>
                              {dataEntryClerks.map((clerk) => (
                                <option key={clerk.id} value={clerk.name}>
                                  {clerk.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        {error && <div className="error-message">{error}</div>}
                      </>
                    )}
                  </div>
                </div>
              </form>
            </div>
            <div className="button-group">
              <PrimaryButton
                text={
                  submitting ? (
                    <AiOutlineLoading3Quarters className="loader" />
                  ) : editingRecordId ? (
                    "Update"
                  ) : (
                    "Submit"
                  )
                }
                onClick={handleSubmit}
                disabled={submitting}
              />
              <PrimaryButton
                text="Cancel"
                onClick={handleCloseModal}
                disabled={submitting}
              />
            </div>
            {(activeTab === "approved"
              ? indexedRecords.length > 0
              : yellowForms.length > 0) && (
              <div className="table-container initialize-site">
                <div className="table-padding-wrapper">
                  <table>
                    <thead>
                      <tr>
                        {activeTab === "approved" ? (
                          <>
                            <th>Book Category</th>
                            <th>Book Type</th>
                            <th>Count</th>
                            <th>Assigned To</th>
                          </>
                        ) : (
                          <>
                            <th>Yellow Form Count</th>
                            <th>Assigned To</th>
                          </>
                        )}
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading
                        ? Array.from({ length: 5 }).map((_, index) => (
                            <tr key={index}>
                              {activeTab === "approved" ? (
                                <>
                                  <td>
                                    <Skeleton width={100} />
                                  </td>
                                  <td>
                                    <Skeleton width={80} />
                                  </td>
                                  <td>
                                    <Skeleton width={60} />
                                  </td>
                                </>
                              ) : (
                                <td>
                                  <Skeleton width={100} />
                                </td>
                              )}
                              <td>
                                <Skeleton width={80} />
                              </td>
                            </tr>
                          ))
                        : activeTab === "approved"
                        ? indexedRecords.map((record) => (
                            <tr key={record.id}>
                              <td>
                                {record.book?.name
                                  ? record.book.name === "Special_category"
                                    ? "Special Category"
                                    : record.book.name
                                  : "-"}
                              </td>
                              <td>{record.book?.bookType || "-"}</td>
                              <td>{record.count || 0}</td>
                              <td>{record.assignedToClerk.name || "-"}</td>
                              <td>
                                <div className="action-buttons edit-delete">
                                  <a
                                    onClick={() => handleEditClick(record)}
                                    aria-label="Edit"
                                    className="icon-button edit-button"
                                    disabled={submitting}
                                  >
                                    <Icon
                                      icon="mdi:book-edit"
                                      width="20"
                                      height="20"
                                      color="#4E7FFF"
                                    />
                                  </a>
                                  <a
                                    onClick={() => handleDeleteClick(record.id)}
                                    aria-label="Delete"
                                    className="icon-button delete-button"
                                    disabled={submitting}
                                  >
                                    <Icon
                                      icon="mingcute:delete-fill"
                                      width="20"
                                      height="20"
                                      color="#E20000"
                                    />
                                  </a>
                                </div>
                              </td>
                            </tr>
                          ))
                        : yellowForms.map((record) => (
                            <tr key={record.id}>
                              <td>{record.yellowFormCount || 0}</td>
                              <td>{record.assignedToClerk.name || "-"}</td>
                              <td>
                                <div className="action-buttons edit-delete">
                                  <a
                                    onClick={() => handleEditClick(record)}
                                    aria-label="Edit"
                                    className="icon-button edit-button"
                                    disabled={submitting}
                                  >
                                    <Icon
                                      icon="mdi:book-edit"
                                      width="20"
                                      height="20"
                                      color="#4E7FFF"
                                    />
                                  </a>
                                  <a
                                    onClick={() => handleDeleteClick(record.id)}
                                    aria-label="Delete"
                                    className="icon-button delete-button"
                                    disabled={submitting}
                                  >
                                    <Icon
                                      icon="mingcute:delete-fill"
                                      width="20"
                                      height="20"
                                      color="#E20000"
                                    />
                                  </a>
                                </div>
                              </td>
                            </tr>
                          ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </Modal>

        <Modal
          show={showConfirmationModal}
          onClose={handleCloseConfirmationModal}
        >
          <div className="content confirmation">
            <div className="icon-container">
              <img
                src={confirmIcon}
                alt="confirm"
                width="57"
                height="57"
                style={{ color: "#078ECE" }}
              />
            </div>
            <div className="text">
              Record has been {editingRecordId ? "updated" : "added"}{" "}
              successfully!
            </div>
          </div>
        </Modal>

        <Modal
          show={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
        >
          <div className="content confirmation">
            <h2>Confirm Delete</h2>
            <div className="text">
              Are you sure you want to delete this record?
            </div>
            <div className="button-group">
              <PrimaryButton
                text={
                  submitting ? (
                    <AiOutlineLoading3Quarters className="loader" />
                  ) : (
                    "Yes"
                  )
                }
                onClick={() => handleDeleteIndexedRecord(recordToDelete)}
                disabled={submitting}
              />
              <PrimaryButton
                text="No"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={submitting}
              />
            </div>
          </div>
        </Modal>

        <Modal
          show={showDeleteSuccess}
          onClose={() => setShowDeleteSuccess(false)}
        >
          <div className="content confirmation">
            <div className="icon-container">
              <img src={confirmIcon} alt="confirm" width="57" height="57" />
            </div>
            <div className="text">Record has been successfully deleted!</div>
          </div>
        </Modal>
      </div>
    </>
  );
};

export default AddIndexedRecords;
